import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { CreditCard, Truck, Check, FileText, Wallet } from "lucide-react";
import axios from "axios";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Modes de livraison disponibles
const DELIVERY_MODE_CONFIG = {
  STORE_DELIVERY: { label: "Standard", icon: "🏪", delay: "1 à 3 jours" },
  TRANSPORT: { label: "Transporteur", icon: "🚚", delay: "3 à 5 jours" },
  PICKUP: { label: "Retrait", icon: "📦", delay: "Immédiat" },
  EXPRESS: { label: "Express (24h)", icon: "⚡", delay: "1 à 24H" },
};


export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, shippingTotal, serviceFees, total, taxes, clearCart, selectedShipping, shippingAddress: contextAddress, deliveryModes } = useCart();

  const { user, isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Billing Address State (Default to shipping)
  const [billingAddress, setBillingAddress] = useState({
    first_name: "", last_name: "", address: "", city: "", postal_code: "", country: "Haïti", phone: ""
  });
  const [useShippingAsBilling, setUseShippingAsBilling] = useState(true);

  // Payment State — MonCash par défaut
  const [paymentMethod, setPaymentMethod] = useState("moncash");
  const [cryptoInfo, setCryptoInfo]             = useState(null);
  const [cryptoNetwork, setCryptoNetwork]       = useState("TRC20");
  const [cryptoSubmitting, setCryptoSubmitting] = useState(false);
  const [pendingOrderId, setPendingOrderId]     = useState(null);

  useEffect(() => {
    // Pre-fill billing with user data if available
    if (user) {
      setBillingAddress(prev => ({
        ...prev,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone
      }));
    }
  }, [user]);

  // If no shipping address from context, redirect to cart
  useEffect(() => {
    if (!contextAddress && items.length > 0) {
      toast.error("Veuillez sélectionner une adresse de livraison");
      navigate("/panier");
    }
  }, [contextAddress, items, navigate]);

  // Charger les infos crypto quand l'utilisateur arrive à l'étape paiement
  useEffect(() => {
    if (step === 2 && !cryptoInfo) {
      axios.get(`${API_URL}/payments/crypto/info`).then(r => setCryptoInfo(r.data)).catch(() => {});
    }
  }, [step, cryptoInfo]);

  const handleBillingChange = (e) => {
    setBillingAddress({ ...billingAddress, [e.target.name]: e.target.value });
  };

  const handleCryptoRedirect = async () => {
    if (!pendingOrderId) return;
    setCryptoSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/payments/crypto/initiate`,
        { order_id: pendingOrderId, network: cryptoNetwork },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      clearCart();
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création du paiement crypto");
      setCryptoSubmitting(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!isAuthenticated || !token) {
      toast.error("Veuillez vous connecter pour commander");
      return;
    }

    setLoading(true);
    try {
      // Filter out invalid items (missing product or id)
      const validItems = items.filter(item => item.product && item.product.id);

      if (validItems.length === 0) {
        toast.error("Panier invalide ou vide");
        setLoading(false);
        return;
      }

      const orderData = {
        items: validItems.map((item) => {
          let booking_date = null;
          // Check if item has booking details (passed as bookingDate in Context)
          if (item.bookingDate && item.bookingDate.bookingDate && item.bookingDate.bookingTime) {
            try {
              // item.bookingDate is the object from ServiceBooking: { bookingDate: Date, bookingTime: "HH:mm", ... }
              const dateObj = new Date(item.bookingDate.bookingDate);
              const [hours, minutes] = item.bookingDate.bookingTime.split(':').map(Number);
              dateObj.setHours(hours, minutes, 0, 0);
              booking_date = dateObj.toISOString();
            } catch (e) {
              console.error("Error formatting booking date", e);
            }
          } else if (item.bookingDate instanceof Date) {
            // Fallback if it was just a date
            booking_date = item.bookingDate.toISOString();
          }

          return {
            product_id: item.product.id,
            quantity: item.quantity,
            booking_date: booking_date,
            delivery_mode: deliveryModes[item.product.id] || null,
          };
        }),
        shipping_address: contextAddress || {},
        billing_address: useShippingAsBilling ? (contextAddress || {}) : billingAddress,
        payment_method: paymentMethod,
        shipping_info: Object.fromEntries(
          Object.entries(selectedShipping || {}).map(([storeId, opt]) => {
            const d = opt?.details || {};
            return [storeId, {
              price:              opt?.price  ?? 0,
              type:               opt?.type   ?? "STORE_STANDARD",
              name:               opt?.name   ?? "",
              urgency:            opt?.urgency ?? "normal",
              // Aplatir les champs carrier depuis details → top-level pour wallet_service
              carrier_fee:        d.carrier_fee        ?? 0,
              store_contribution: d.store_contribution ?? 0,
              contribution_pct:   d.contribution_pct   ?? 0,
              carrier_name:       d.carrier_name       ?? null,
              covered_by:         (d.carrier_fee ?? 0) > 0 ? "CARRIER" : "CUSTOMER",
            }];
          })
        )
      };

      console.log("Submitting order payload:", JSON.stringify(orderData));

      const orderRes = await axios.post(`${API_URL}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const createdOrder = orderRes.data;

      // ── MonCash : ouvrir le portail dans un nouvel onglet ───────────────
      if (paymentMethod === "moncash") {
        const mcRes = await axios.post(
          `${API_URL}/payments/moncash/order/initiate`,
          { order_id: createdOrder.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        clearCart();
        window.open(mcRes.data.payment_url, "_blank", "noopener,noreferrer");
        toast.success("MonCash ouvert dans un nouvel onglet. Finalisez le paiement là-bas.");
        navigate("/commandes");
        return;
      }

      // ── Crypto USDT : montrer le formulaire de confirmation ───────────────
      if (paymentMethod === "crypto") {
        setPendingOrderId(createdOrder.id);
        setStep(3);
        setLoading(false);
        return;
      }

      clearCart();
      toast.success("Commande passée avec succès !");
      navigate(`/commandes`);
    } catch (error) {
      // FIX: Ensure message is a string
      let message = "Erreur lors de la commande";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        message = typeof detail === 'string' ? detail : JSON.stringify(detail);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate("/panier");
    return null;
  }

  const displayTotal = total;

  return (
    <div className="min-h-screen bg-background">
      {/* Overlay de traitement commande */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <p className="font-semibold text-gray-800">Traitement de votre commande…</p>
            <p className="text-xs text-gray-400">Veuillez ne pas fermer cette page</p>
          </div>
        </div>
      )}
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl py-8">
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-8">
          Finaliser la commande
        </h1>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {step > 1 ? <Check className="h-5 w-5" /> : "1"}
            </div>
            <span className="ml-2 font-medium">Facturation</span>
          </div>
          <div className="w-16 md:w-32 h-0.5 bg-muted mx-4"></div>
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {step > 2 ? <Check className="h-5 w-5" /> : "2"}
            </div>
            <span className="ml-2 font-medium">Paiement</span>
          </div>
          {/* Step 3 : confirmation crypto (affiché seulement quand pertinent) */}
          {(step === 3 || paymentMethod === "crypto") && (
            <>
              <div className="w-16 md:w-32 h-0.5 bg-muted mx-4"></div>
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  3
                </div>
                <span className="ml-2 font-medium">Crypto</span>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">

            {/* STEP 1: BILLING & CONFIRMATION */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Shipping Address Confirmation */}
                <Card className="border-green-200 bg-green-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-green-800">
                      <Truck className="h-4 w-4" /> Adresse de livraison (Sélectionnée)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="font-medium text-gray-900">{contextAddress?.name}</div>
                    <div className="text-gray-700">{contextAddress?.address}</div>
                    <Button variant="link" className="p-0 h-auto text-green-700 mt-2" onClick={() => navigate("/panier")}>
                      Modifier
                    </Button>
                  </CardContent>
                </Card>

                {/* Billing Address Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Adresse de facturation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2 border p-3 rounded-lg bg-gray-50">
                      <input
                        type="checkbox"
                        id="same_address"
                        checked={useShippingAsBilling}
                        onChange={(e) => setUseShippingAsBilling(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="same_address" className="cursor-pointer font-normal">Identique à l'adresse de livraison</Label>
                    </div>

                    {!useShippingAsBilling && (
                      <div className="grid grid-cols-1 gap-4 animate-in fade-in slide-in-from-top-2">
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Prénom</Label>
                              <Input name="first_name" value={billingAddress.first_name} onChange={handleBillingChange} />
                            </div>
                            <div className="space-y-2">
                              <Label>Nom</Label>
                              <Input name="last_name" value={billingAddress.last_name} onChange={handleBillingChange} />
                            </div>
                          </div>
                          <AddressFormGroup
                            addressData={{
                              countryCode: billingAddress.countryCode || 'ht',
                              address: billingAddress.address,
                              city: billingAddress.city,
                              region: billingAddress.region,
                              zipCode: billingAddress.zipCode,
                              address_details: billingAddress.address_details,
                              lat: billingAddress.lat,
                              lng: billingAddress.lng
                            }}
                            onChange={(newData) => {
                              setBillingAddress(prev => ({
                                ...prev,
                                countryCode: newData.countryCode,
                                address: newData.address,
                                city: newData.city,
                                region: newData.region,
                                zipCode: newData.zipCode,
                                address_details: newData.address_details,
                                lat: newData.lat,
                                lng: newData.lng
                              }));
                            }}
                          />
                          <div className="space-y-2">
                            <Label>Téléphone</Label>
                            <Input name="phone" value={billingAddress.phone} onChange={handleBillingChange} />
                          </div>
                        </div>
                      </div>
                    )}

                    <Button className="w-full mt-4" onClick={() => setStep(2)}>
                      Continuer vers le paiement
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* STEP 2: PAYMENT */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Paiement sécurisé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">

                    {/* MonCash — option par défaut */}
                    <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${paymentMethod === "moncash" ? "border-orange-500 bg-orange-50/50" : "border-gray-200 hover:border-orange-200"}`}
                      onClick={() => setPaymentMethod("moncash")}>
                      <RadioGroupItem value="moncash" id="moncash" />
                      <Label htmlFor="moncash" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 font-semibold text-base">
                          <span className="text-xl">📱</span> MonCash
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">Paiement mobile Digicel — vous serez redirigé vers MonCash</p>
                      </Label>
                      {paymentMethod === "moncash" && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-medium">Recommandé</span>}
                    </div>

                    {/* USDT Crypto */}
                    {cryptoInfo?.available && (
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${paymentMethod === "crypto" ? "border-blue-500 bg-blue-50/50" : "border-gray-200 hover:border-blue-200"}`}
                        onClick={() => setPaymentMethod("crypto")}>
                        <RadioGroupItem value="crypto" id="crypto" />
                        <Label htmlFor="crypto" className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2 font-semibold text-base">
                            <span className="text-xl">💎</span> USDT (Crypto)
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">TRC20 ou BEP20 — envoi manuel + soumission du hash</p>
                        </Label>
                      </div>
                    )}

                    {/* Carte Bancaire */}
                    <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${paymentMethod === "card" ? "border-gray-500 bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                      onClick={() => setPaymentMethod("card")}>
                      <RadioGroupItem value="card" id="card" />
                      <Label htmlFor="card" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2 font-medium">
                          <CreditCard className="h-4 w-4" /> Carte Bancaire
                        </div>
                      </Label>
                    </div>

                    {/* Portefeuille BuyMore */}
                    <div className={`flex items-center space-x-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${paymentMethod === "wallet" ? "border-gray-500 bg-gray-50" : "border-gray-200"} ${user?.wallet_balance < displayTotal ? "opacity-50 cursor-not-allowed" : "hover:border-gray-300"}`}
                      onClick={() => user?.wallet_balance >= displayTotal && setPaymentMethod("wallet")}>
                      <RadioGroupItem value="wallet" id="wallet" disabled={user?.wallet_balance < displayTotal} />
                      <Label htmlFor="wallet" className={`flex-1 ${user?.wallet_balance < displayTotal ? "cursor-not-allowed" : "cursor-pointer"}`}>
                        <div className="flex items-center gap-2 font-medium">
                          <Wallet className="h-4 w-4" /> Portefeuille BuyMore
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Solde : <strong>{user?.wallet_balance?.toFixed(2) || "0.00"} HTG</strong>
                          {user?.wallet_balance < displayTotal && <span className="text-red-500 ml-2">(Insuffisant)</span>}
                        </p>
                      </Label>
                    </div>
                  </RadioGroup>

                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Retour</Button>
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                      onClick={handleSubmitOrder}
                      disabled={loading}
                    >
                      {loading ? "Traitement…" : paymentMethod === "moncash"
                        ? `Payer via MonCash — ${displayTotal.toFixed(2)} HTG`
                        : paymentMethod === "crypto"
                          ? `Continuer vers la crypto`
                          : `Payer ${displayTotal.toFixed(2)} HTG`}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 3: CHOIX DU RÉSEAU CRYPTO → CoinPayments */}
            {step === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-xl">💎</span> Paiement USDT via CoinPayments
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">

                  {/* Montant à payer */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center">
                    <p className="text-sm text-blue-600 mb-1">Montant total à payer</p>
                    <p className="text-4xl font-bold text-blue-900">
                      {cryptoInfo
                        ? (displayTotal / cryptoInfo.rate_htg_per_usdt).toFixed(2)
                        : "—"
                      }
                      <span className="text-xl font-medium ml-2">USDT</span>
                    </p>
                    <p className="text-sm text-blue-700 mt-1 font-medium">
                      {displayTotal.toFixed(2)} HTG
                    </p>
                    {cryptoInfo && (
                      <p className="text-xs text-blue-500 mt-1">
                        Taux appliqué : 1 USDT = {cryptoInfo.rate_htg_per_usdt} HTG
                      </p>
                    )}
                  </div>

                  {/* Sélection du réseau — liste déroulante */}
                  <div className="space-y-2">
                    <Label htmlFor="crypto-network" className="text-sm font-semibold block">
                      Réseau de paiement
                    </Label>
                    <select
                      id="crypto-network"
                      value={cryptoNetwork}
                      onChange={e => setCryptoNetwork(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="TRC20">USDT TRC20 — Tron (TRON)</option>
                      <option value="BEP20">USDT BEP20 — BNB Smart Chain (BSC)</option>
                    </select>
                    <p className="text-xs text-gray-500">
                      Choisissez le réseau depuis lequel vous allez envoyer les USDT.
                    </p>
                  </div>

                  <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2.5">
                    Vous serez redirigé vers <strong>CoinPayments</strong> pour finaliser le paiement en toute sécurité.
                    La confirmation est automatique après réception des fonds.
                  </p>

                  <div className="flex gap-3 pt-1">
                    <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                      Retour
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                      onClick={handleCryptoRedirect}
                      disabled={cryptoSubmitting}
                    >
                      {cryptoSubmitting
                        ? "Redirection en cours…"
                        : `Payer ${cryptoInfo ? (displayTotal / cryptoInfo.rate_htg_per_usdt).toFixed(2) + " USDT" : ""} via CoinPayments`
                      }
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 bg-gray-50/50">
              <CardHeader>
                <CardTitle>Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item) => (
                  <div key={item.product.id} className="space-y-2 pb-3 border-b last:border-0">
                    <div className="flex gap-3 text-sm">
                      <div className="w-12 h-12 rounded bg-white border flex-shrink-0 overflow-hidden">
                        <img src={item.product.images?.[0]} className="w-full h-full object-cover" alt="" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium line-clamp-1">{item.product.title}</p>
                        <p className="text-gray-500">x{item.quantity}</p>
                      </div>
                      <p className="font-medium">
                        {((item.product.discount_price || item.product.price) * item.quantity).toFixed(2)} HTG
                      </p>
                    </div>
                    {/* Mode de livraison sélectionné — lecture seule */}
                    {deliveryModes[item.product.id] && (() => {
                      const mode = deliveryModes[item.product.id];
                      const cfg = DELIVERY_MODE_CONFIG[mode] || { label: mode, icon: "📦", delay: "" };
                      const storeCriteria = item.product?.store?.delivery_criteria;
                      let displayDelay = cfg.delay;
                      if (storeCriteria) {
                        if (mode === "STORE_DELIVERY" && storeCriteria.std_delivery_time) displayDelay = storeCriteria.std_delivery_time;
                        if (mode === "EXPRESS" && storeCriteria.express_delivery_time) displayDelay = storeCriteria.express_delivery_time;
                      }
                      return (
                        <div className="ml-1 mt-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20 font-medium">
                            <span>{cfg.icon}</span>
                            <span>{cfg.label}</span>
                            {displayDelay && <span className="opacity-70">({displayDelay})</span>}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                ))}

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Sous-total</span>
                    <span>{subtotal.toFixed(2)} HTG</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Livraison</span>
                    <span className={shippingTotal === 0 ? "text-green-600 font-medium" : ""}>
                      {shippingTotal === 0 ? "Gratuite" : `${shippingTotal.toFixed(2)} HTG`}
                    </span>
                  </div>
                  {serviceFees > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Frais de déplacement</span>
                      <span>{serviceFees.toFixed(2)} HTG</span>
                    </div>
                  )}
                  {taxes > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Taxes estimées</span>
                      <span>{taxes.toFixed(2)} HTG</span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between font-bold text-xl">
                  <span>Total</span>
                  <span className="text-orange-600">{displayTotal.toFixed(2)} HTG</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
